import { tenderViewsService } from '@/services/tender-views.service';
import { i18n } from '@/plugins/i18n';
import {
  VIEW_NAME_MAX_LENGTH,
  VIEW_SCOPE,
  areFiltersEqual,
  isPresetView,
  stripTransientFilters,
  viewFiltersToWorking,
  workingToViewFilters,
} from '@/models/tender-views';

const initialState = () => ({
  // Views as returned by the API (own private + tenant shared + presets), pre-sorted.
  views: [],
  activeViewId: null,
  // working baseline: the active view's saved filters in client/editor shape (dirty comparison)
  baselineFilters: {},
  // per-view result counts for tab badges: { [viewId]: number }. Currently not fetched.
  viewCounts: {},
  status: {
    error: null,
    pending: true,
    resolved: false,
    rejected: false,
  },
});

const state = initialState();

// Create a view, then make it the active one.
function createAndSelectView({ commit, dispatch }, { name, scope, preset, filters, aviseurEnabled }) {
  return tenderViewsService.create({ name, scope, preset, filters }).then((created) => {
    commit('UPSERT_VIEW', created);
    // Aviseur is a separate per-user subscription endpoint — enable it after creation if requested.
    const ready = aviseurEnabled
      ? tenderViewsService.setAviseur(created.id, true).then(() => {
          commit('SET_VIEW_AVISEUR', { viewId: created.id, enabled: true });
        })
      : Promise.resolve();
    return ready.then(() => dispatch('selectView', created.id)).then(() => created);
  });
}

const actions = {
  loadViews({ commit }) {
    commit('LOAD_VIEWS');
    return tenderViewsService.list().then(
      (views) => {
        commit('LOAD_VIEWS_SUCCESS', views);
        return views;
      },
      (error) => commit('ERROR', error),
    );
  },

  // Switch the active view and push its saved filters into the editor working set.
  selectView({ commit, getters, dispatch }, viewId) {
    const view = getters.viewById(viewId);
    if (!view) return;
    commit('SET_ACTIVE_VIEW', viewId);
    const working = viewFiltersToWorking(view.filters || {});
    commit('SET_BASELINE_FILTERS', working);
    dispatch('searchFilters/loadAllFilters', working, { root: true });
  },

  // Persist the current working filters onto the active (editable) view.
  updateActiveView({ commit, getters, rootGetters }) {
    const view = getters.activeView;
    if (!view || !view.canEdit) return Promise.resolve();
    const filters = stripTransientFilters(workingToViewFilters(rootGetters['searchFilters/searchFilters']));
    return tenderViewsService.update(view.id, { filters }).then((updated) => {
      commit('UPSERT_VIEW', updated);
      commit('SET_BASELINE_FILTERS', viewFiltersToWorking(updated.filters || {}));
      return updated;
    });
  },

  // "New view": sends only the dialog's choice and lets the backend fill every other filter with its
  // defaults (statuses ["active"], closingBefore ANY_TIME, which the search runs as "closing in the
  // future") — never the active view's filters (that's saveAsNewView).
  createView(
    context,
    { name, scope = VIEW_SCOPE.PRIVATE, preset = false, allKeywords = false, aviseurEnabled = false } = {},
  ) {
    return createAndSelectView(context, { name, scope, preset, filters: { allKeywords }, aviseurEnabled });
  },

  // Save the current working filters as a brand-new private view (the "save a copy" path).
  saveAsNewView(
    context,
    { name, scope = VIEW_SCOPE.PRIVATE, preset = false, allKeywords, aviseurEnabled = false } = {},
  ) {
    const filters = stripTransientFilters(workingToViewFilters(context.rootGetters['searchFilters/searchFilters']));
    if (allKeywords !== undefined) filters.allKeywords = allKeywords;
    return createAndSelectView(context, { name, scope, preset, filters, aviseurEnabled });
  },

  renameView({ commit, getters }, { viewId, name }) {
    const view = getters.viewById(viewId);
    if (!view || !view.canEdit) return Promise.resolve();
    return tenderViewsService.update(viewId, { name }).then((updated) => commit('UPSERT_VIEW', updated));
  },

  // Duplicate a view from its SAVED filters (not the working edits) as a new private view.
  duplicateView(context, viewId) {
    const view = context.getters.viewById(viewId);
    if (!view) return Promise.resolve();
    // Append the copy suffix, truncating the base name so the total stays within the DB cap.
    const suffix = ` (${i18n.t('page.tenders.views.duplicate-suffix')})`;
    const base = view.name.slice(0, Math.max(0, VIEW_NAME_MAX_LENGTH - suffix.length)).trimEnd();
    const name = `${base}${suffix}`;
    return createAndSelectView(context, { name, scope: VIEW_SCOPE.PRIVATE, preset: false, filters: view.filters });
  },

  toggleScope({ commit, getters }, viewId) {
    const view = getters.viewById(viewId);
    if (!view || !view.canEdit) return Promise.resolve();
    const scope = view.scope === VIEW_SCOPE.SHARED ? VIEW_SCOPE.PRIVATE : VIEW_SCOPE.SHARED;
    return tenderViewsService.update(viewId, { scope }).then((updated) => commit('UPSERT_VIEW', updated));
  },

  toggleAviseur({ commit, getters }, viewId) {
    const view = getters.viewById(viewId);
    if (!view) return Promise.resolve();
    const enabled = !view.aviseurEnabled;
    return tenderViewsService.setAviseur(viewId, enabled).then(() => commit('SET_VIEW_AVISEUR', { viewId, enabled }));
  },

  removeView({ commit, getters, dispatch }, viewId) {
    const view = getters.viewById(viewId);
    if (!view || !view.canEdit) return Promise.resolve();
    return tenderViewsService.remove(viewId).then(() => {
      const index = getters.views.findIndex((v) => v.id === viewId);
      const remaining = getters.views.filter((v) => v.id !== viewId);
      commit('REMOVE_VIEW', viewId);
      if (state.activeViewId === viewId) {
        if (remaining.length) {
          // Fall back to the neighbouring view — the previous one, or the first if we deleted the
          // first — so the user stays near where they were instead of jumping to the first tab.
          dispatch('selectView', remaining[Math.max(0, index - 1)].id);
        } else {
          commit('SET_ACTIVE_VIEW', null);
        }
      }
    });
  },

  // Persist this user's personal order of all their visible views (full ordered id list).
  reorder({ commit, getters }, orderedIds) {
    commit('SET_VIEWS_ORDER', orderedIds);
    const presetIds = getters.views.filter(isPresetView).map((v) => v.id);
    // Admin preset order is a separate tenant-wide list.
    return Promise.all([
      tenderViewsService.reorder(orderedIds),
      presetIds.length ? tenderViewsService.reorderPresets(presetIds) : Promise.resolve(),
    ]);
  },

  // Batched per-view tab-badge counts. Available but not currently dispatched.
  loadCounts({ commit, getters }) {
    const viewIds = getters.views.map((v) => v.id);
    if (!viewIds.length) return Promise.resolve();
    return tenderViewsService.getViewCounts(viewIds).then(
      (counts) => commit('SET_VIEW_COUNTS', counts),
      () => {},
    );
  },

  // Discard working edits, restoring the active view's saved filters.
  resetActiveView({ getters, dispatch }) {
    const view = getters.activeView;
    if (!view) return;
    dispatch('searchFilters/loadAllFilters', viewFiltersToWorking(view.filters || {}), { root: true });
  },
};

const mutations = {
  LOAD_VIEWS(state) {
    state.status = { error: null, pending: true, resolved: false, rejected: false };
  },
  LOAD_VIEWS_SUCCESS(state, views) {
    state.status = { error: null, pending: false, resolved: true, rejected: false };
    state.views = views || [];
  },
  ERROR(state, error) {
    state.status = { ...state.status, pending: false, rejected: true, error };
  },
  SET_ACTIVE_VIEW(state, viewId) {
    state.activeViewId = viewId;
  },
  SET_BASELINE_FILTERS(state, filters) {
    state.baselineFilters = filters;
  },
  SET_VIEW_COUNTS(state, counts) {
    const map = {};
    (counts || []).forEach((c) => {
      map[c.viewId] = c.count;
    });
    state.viewCounts = map;
  },
  SET_VIEW_AVISEUR(state, { viewId, enabled }) {
    const view = state.views.find((v) => v.id === viewId);
    if (view) view.aviseurEnabled = enabled;
  },
  UPSERT_VIEW(state, view) {
    const index = state.views.findIndex((v) => v.id === view.id);
    if (index === -1) state.views.push(view);
    else state.views.splice(index, 1, view);
  },
  REMOVE_VIEW(state, viewId) {
    state.views = state.views.filter((v) => v.id !== viewId);
  },
  SET_VIEWS_ORDER(state, orderedIds) {
    const byId = new Map(state.views.map((v) => [v.id, v]));
    const reordered = orderedIds.map((id) => byId.get(id)).filter(Boolean);
    // keep any views not present in orderedIds at the end
    state.views.forEach((v) => {
      if (!orderedIds.includes(v.id)) reordered.push(v);
    });
    state.views = reordered;
  },
  RESET(state) {
    const initial = initialState();
    Object.keys(initial).forEach((key) => {
      state[key] = initial[key];
    });
  },
};

const getters = {
  views: (state) => state.views,
  // Kept for backward compat with components that referenced `allViews`.
  allViews: (state) => state.views,
  viewById: (state) => (id) => state.views.find((v) => v.id === id) || null,
  activeViewId: (state) => state.activeViewId,
  activeView: (state, getters) => getters.viewById(state.activeViewId),
  viewCount: (state) => (viewId) => viewId in state.viewCounts ? state.viewCounts[viewId] : null,

  // Dirty = working filters differ from the active view's saved baseline.
  isActiveViewDirty: (state, getters, rootState, rootGetters) => {
    if (!getters.activeView) return false;
    return !areFiltersEqual(rootGetters['searchFilters/searchFilters'], state.baselineFilters);
  },

  // Server is source of truth for edit rights (owner or tenant admin); DTO carries it.
  canEditView: () => (view) => !!(view && view.canEdit),
  canEditActiveView: (state, getters) => !!(getters.activeView && getters.activeView.canEdit),

  pending: (state) => state.status.pending,
  error: (state) => state.status.error,
};

export const tenderViews = {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
